import { GAME_CONFIG } from '../config';
import { GameState } from './GameState';
import { GameUI } from '../components/GameUI';
import { SceneManager } from './SceneManager';
import { LLMService } from '../services/LLMService';
import { InviteCodeModal } from '../components/InviteCodeModal';

export class Game {
    private gameState: GameState;
    private ui: GameUI;
    private sceneManager: SceneManager;
    private llmService: LLMService;
    private currentModel: string = GAME_CONFIG.defaultModel;

    constructor() {
        this.gameState = new GameState();
        this.ui = new GameUI();
        this.llmService = LLMService.getInstance(this.currentModel);
        this.sceneManager = new SceneManager(this.llmService, this.gameState, this.ui);

        // 初始化所有组件
        this.initializeEventListeners();
        this.initializeModelSelector();
        this.initialize();

        // 显示模型通知
        this.ui.showNotification('当前使用 302AI 模型，可在顶部切换模型。', 'info', 5000);

        // 尝试自动加载上次的游戏记录
        this.autoLoadLastGame();

    }

    private initializeEventListeners(): void {
        // 保存游戏事件监听
        document.addEventListener('save-game', () => {
            this.handleSaveGame();
        });

        // 加载游戏事件监听
        document.addEventListener('load-game', () => {
            this.handleLoadGame();
        });

        // 自定义输入事件监听
        document.addEventListener('custom-input', (event: Event) => {
            const customEvent = event as CustomEvent;
            if (customEvent.detail?.text) {
                this.handleCustomInput(customEvent.detail.text);
            }
        });

        // 如果开启了调试模式，添加调试相关的事件监听
        if (GAME_CONFIG.debugMode) {
            document.addEventListener('debug-command', (event: Event) => {
                const customEvent = event as CustomEvent;
                if (customEvent.detail?.command) {
                    console.log('执行调试命令:', customEvent.detail.command);
                }
            });
        }
    }

    private initializeModelSelector(): void {
        const modelSelector = this.ui.createModelSelector((modelId: string) => {
            this.currentModel = modelId;
            this.llmService.setModel(modelId);
            console.log('已切换到模型:', modelId);
        });

        // 将模型选择器添加到游戏界面
        const gameContainer = document.getElementById('game-container');
        if (gameContainer) {
            // 在游戏容器的开头插入模型选择器
            gameContainer.insertBefore(modelSelector, gameContainer.firstChild);
        }
    }

    private initialize(): void {
        // 添加导出历史记录事件监听
        document.addEventListener('export-history-requested', () => {
            const historyData = this.gameState.exportHistory();
            this.downloadHistory(historyData);
        });

        // 添加导入历史记录事件监听
        document.addEventListener('import-history', (event: Event) => {
            const customEvent = event as CustomEvent;
            if (customEvent.detail?.data) {
                if (this.gameState.importHistory(customEvent.detail.data)) {
                    // 先更新对话历史
                    this.ui.updateDialogueHistory(this.gameState.getDialogueHistory());
                    this.ui.showNotification('历史记录已导入！', 'success', 2000);
                    // 然后显示当前场景
                    this.sceneManager.displayScene(
                        this.gameState.getState().currentScene,
                        null,
                        true
                    );
                } else {
                    this.ui.showNotification('导入失败：无效的历史记录格式', 'error', 3000);
                }
            }
        });

        // 添加重新开始游戏事件监听
        document.addEventListener('restart-game', async () => {
            const confirmRestart = confirm('确定要重新开始游戏吗？当前进度将会丢失。');
            if (confirmRestart) {
                this.gameState.reset();
                localStorage.removeItem(GAME_CONFIG.saveKey);
                // 清空对话历史
                this.ui.updateDialogueHistory([]);
                this.ui.showNotification('游戏已重置', 'info', 2000);
                await this.sceneManager.displayScene(GAME_CONFIG.defaultScene);
            }
        });
    }

    private downloadHistory(data: string): void {
        const blob = new Blob([data], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cyberpunk-game-history-${new Date().toISOString()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    async start(): Promise<void> {
        try {
            await this.startGame();
        } catch (error) {
            console.error('游戏启动失败:', error);
            this.ui.showError('游戏启动失败，请刷新页面重试。');
        }
    }

    private async startGame(): Promise<void> {
        // 显示初始场景
        await this.sceneManager.displayScene(GAME_CONFIG.defaultScene);
    }

    private handleSaveGame(): void {
        if (this.gameState.save()) {
            this.ui.showNotification('游戏已保存！', 'success', 2000);
        } else {
            this.ui.showNotification('保存游戏失败！', 'error', 3000);
        }
    }

    private handleLoadGame(): void {
        if (this.gameState.load()) {
            this.sceneManager.displayScene(this.gameState.getState().currentScene, null, true);
            this.ui.showNotification('游戏已加载！', 'success', 2000);
        } else {
            this.ui.showNotification('没有找到保存的游戏或加载失败！', 'error', 3000);
        }
    }

    private handleCustomInput(text: string): void {
        this.ui.disableAllInputs();
        const customChoice = {
            text,
            next_scene: this.gameState.getState().currentScene,
            requirements: null,
            is_custom: true
        };
        this.sceneManager.displayScene(this.gameState.getState().currentScene, customChoice as any);
        this.ui.enableAllInputs();
    }

    private async autoLoadLastGame(): Promise<void> {
        try {
            // 检查是否有存档
            if (localStorage.getItem(GAME_CONFIG.saveKey)) {
                // 显示加载提示
                this.ui.showNotification('发现游戏存档...', 'info', 3000);
                
                // 显示选择弹窗
                this.ui.createLoadGameModal(
                    // 继续游戏回调
                    async () => {
                        if (this.gameState.load()) {
                            this.ui.showNotification('已加载游戏存档', 'success', 2000);
                            // 先更新对话历史
                            this.ui.updateDialogueHistory(this.gameState.getDialogueHistory());
                            // 然后显示当前场景
                            await this.sceneManager.displayScene(
                                this.gameState.getState().currentScene,
                                null,
                                true
                            );
                        } else {
                            this.ui.showNotification('加载存档失败', 'error', 3000);
                            await this.sceneManager.displayScene(GAME_CONFIG.defaultScene);
                        }
                    },
                    // 新游戏回调
                    async () => {
                        this.gameState.reset();
                        localStorage.removeItem(GAME_CONFIG.saveKey);
                        await this.sceneManager.displayScene(GAME_CONFIG.defaultScene);
                    }
                );
            } else {
                // 没有存档，直接开始新游戏
                await this.sceneManager.displayScene(GAME_CONFIG.defaultScene);
            }
        } catch (error) {
            console.error('自动加载游戏失败:', error);
            this.ui.showNotification('加载游戏失败，开始新游戏', 'error', 3000);
            await this.sceneManager.displayScene(GAME_CONFIG.defaultScene);
        }
    }
} 
